
import React, { useState, useEffect } from 'react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { ExternalLink, AlertCircle, RefreshCw } from 'lucide-react';
import { getGoogleMapsApiKey } from '@/services/googleMapsService';
import { isStreetViewUrl, getStreetViewCoords, getStreetViewParams } from '@/utils/mapUtils';
import InteractiveStreetView from './InteractiveStreetView';

interface MapPreviewProps {
  url: string;
  onViewChange?: (view: { lat: number; lng: number; heading: number; pitch: number }) => void;
}

const MapPreview: React.FC<MapPreviewProps> = ({ url, onViewChange }) => {
  const [embedError, setEmbedError] = useState(false);
  const [mapsApiKey, setMapsApiKey] = useState<string | null>(null);
  const [isLoadingKey, setIsLoadingKey] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        console.log('🗺️ MapPreview: Fetching Google Maps API key...');
        const key = await getGoogleMapsApiKey();
        console.log('🗺️ MapPreview: API key received:', !!key);
        setMapsApiKey(key);
      } catch (error) {
        console.error('🗺️ MapPreview: Failed to fetch Google Maps API key:', error);
        setMapsApiKey(null);
      } finally {
        setIsLoadingKey(false);
      }
    };

    fetchApiKey();
  }, []);

  // Use the centralized Street View detection
  const isStreetViewURL = (url: string) => {
    const result = isStreetViewUrl(url);
    console.log('🗺️ MapPreview: Street View detection for URL:', url, '-> Result:', result);
    return result;
  };

  const coordinates = getStreetViewCoords(url);
  console.log('🗺️ MapPreview: Extracted coordinates:', coordinates);

  const handleRetry = () => {
    console.log('🗺️ MapPreview: Retrying map load...');
    setEmbedError(false);
    setIframeLoaded(false);
    setRetryCount(prev => prev + 1);
  };
  
  if (!coordinates) {
    return (
      <div className="my-3 border rounded-lg overflow-hidden bg-card shadow-sm">
        <div className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-destructive/10 rounded flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-destructive" />
              </div>
              <div>
                <p className="text-sm font-medium text-card-foreground">Invalid Map URL</p>
                <p className="text-xs text-muted-foreground">Could not extract coordinates from URL</p>
              </div>
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
            >
              Open
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while fetching API key
  if (isLoadingKey) {
    return (
      <div className="my-3 border rounded-lg overflow-hidden bg-card shadow-sm">
        <div className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center animate-spin">
                <RefreshCw className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-card-foreground">Loading Map Preview</p>
                <p className="text-xs text-muted-foreground">Fetching API key...</p>
              </div>
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
            >
              Open
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Create appropriate embed URL based on whether it's street view or not
  let embedUrl;
  let mapPinUrl; // Separate URL for map pin link
  
  if (isStreetViewURL(url)) {
    console.log('🗺️ MapPreview: Creating Street View embed URL');
    const streetViewParams = getStreetViewParams(url);
    
    // Use panorama ID if available for exact same view
    if (streetViewParams.panoId) {
      embedUrl = `https://www.google.com/maps/embed/v1/streetview?key=${mapsApiKey}&pano=${streetViewParams.panoId}&heading=${streetViewParams.heading}&pitch=${streetViewParams.pitch}&fov=${streetViewParams.fov}`;
      console.log('🗺️ MapPreview: Generated Street View embed URL with pano ID:', embedUrl);
    } else {
      embedUrl = `https://www.google.com/maps/embed/v1/streetview?key=${mapsApiKey}&location=${coordinates!.lat},${coordinates!.lng}&heading=${streetViewParams.heading}&pitch=${streetViewParams.pitch}&fov=${streetViewParams.fov}`;
      console.log('🗺️ MapPreview: Generated Street View embed URL with coordinates:', embedUrl);
    }
    
    // Create a standard map view for the pin link (zoomed in)
    mapPinUrl = `https://www.google.com/maps/@${coordinates!.lat},${coordinates!.lng},17z`;
  } else {
    console.log('🗺️ MapPreview: Creating standard map embed URL');
    embedUrl = `https://www.google.com/maps/embed/v1/view?key=${mapsApiKey}&center=${coordinates!.lat},${coordinates!.lng}&zoom=15&maptype=roadmap`;
    mapPinUrl = `https://www.google.com/maps/@${coordinates!.lat},${coordinates!.lng},15z`;
    console.log('🗺️ MapPreview: Generated Map embed URL:', embedUrl);
  }

  if (embedError || !mapsApiKey) {
    return (
      <div className="my-3 border border-yellow-500/20 rounded-lg overflow-hidden bg-yellow-500/10 shadow-sm">
        <div className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-yellow-500/20 rounded flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-card-foreground">
                  {!mapsApiKey ? 'Google Maps API Key Required' : 'Map Load Error'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {!mapsApiKey ? 'API key unavailable' : `Failed to load map (attempt ${retryCount + 1})`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {embedError && retryCount < 3 && (
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400 hover:text-yellow-700 dark:hover:text-yellow-300 transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Retry
                </button>
              )}
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
              >
                Open
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
          {!mapsApiKey && (
            <div className="mt-2 text-xs text-muted-foreground">
              <p>To enable map previews, configure a Google Maps API key in Supabase.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="my-3 border rounded-lg overflow-hidden bg-card shadow-sm">
      <AspectRatio ratio={16 / 9} className="bg-muted relative">
        {isStreetViewURL(url) ? (
          <>
            {console.log('🗺️ MapPreview: Rendering InteractiveStreetView with coordinates:', coordinates)}
            <InteractiveStreetView
              apiKey={mapsApiKey!}
              initialCoords={coordinates!}
              panoId={getStreetViewParams(url).panoId}
              heading={getStreetViewParams(url).heading}
              pitch={getStreetViewParams(url).pitch}
              onViewChange={onViewChange}
            />
          </>
        ) : (
          <>
            {!iframeLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-muted">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading map...</span>
                </div>
              </div>
            )}
            <iframe
              key={`${embedUrl}-${retryCount}`}
              src={embedUrl}
              className="w-full h-full"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Map Preview"
              onLoad={() => {
                console.log('🗺️ MapPreview: Iframe loaded successfully');
                setIframeLoaded(true);
              }}
              onError={(e) => {
                console.error('🗺️ MapPreview: Iframe load error:', e);
                setEmbedError(true);
                setIframeLoaded(true);
              }}
            />
          </>
        )}
      </AspectRatio>
      <div className="p-3 border-t">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <a
              href={mapPinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center hover:bg-primary/20 transition-colors"
              title="View on Google Maps"
            >
              <span className="text-primary text-xs font-medium">📍</span>
            </a>
            <div>
              <p className="text-sm font-medium text-card-foreground">
                {isStreetViewURL(url) ? 'Google Street View' : 'Google Maps'}
              </p>
              <p className="text-xs text-muted-foreground">
                {coordinates!.lat.toFixed(6)}, {coordinates!.lng.toFixed(6)}
              </p>
            </div>
          </div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
          >
            Open
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default MapPreview;
